<script setup lang="ts">
import { computed } from 'vue';
import { useRouter, type RouteLocationRaw } from 'vue-router';
import { navLabels } from '@/components/design/domain/navigation/navLabels';

/**
 * The trail every templating page opens with: Organisation → Templates → the
 * template → its variant → the page. Each page used to spell its own
 * out, which is how a crumb ends up clickable on one page and inert on the next.
 *
 * A crumb is a link exactly when there is somewhere further along the trail to
 * come back from, so the last one rendered is always plain text.
 *
 * Crumbs only, no breadcrumb of its own: mount it inside a layout's
 * `#breadcrumb` slot, which supplies the bar and the `GaiaBreadcrumb`.
 */
const props = defineProps<{
  /** Omit on the list page — there the list itself is where you are. */
  templateUid?: string;
  /** The template's name, or undefined while it is still loading. */
  templateName?: string;
  /** Omit on the template page; with one, the template crumb links back to it. */
  variantUid?: string;
  /** The variant's name, or undefined while loading. */
  variantName?: string;
  /** A sub page's name. With one, the variant crumb links to its detail. */
  page?: string;
  /**
   * How a crumb navigates. Defaults to `router.push`; the authoring editor
   * passes its dirty-guarded `leave` so a crumb still asks before discarding.
   */
  navigate?: (to: RouteLocationRaw) => void;
}>();

const router = useRouter();

const listRoute: RouteLocationRaw = { name: 'contract-generation-templates' };
const templateRoute = computed<RouteLocationRaw>(() => ({
  name: 'contract-generation-template-detail',
  params: { uid: props.templateUid },
}));
const variantRoute = computed<RouteLocationRaw>(() => ({
  name: 'contract-generation-template-variant-detail',
  params: { uid: props.templateUid, variantUid: props.variantUid },
}));

/**
 * Crumbs are real links (new tab / cmd-click work); a plain left click is
 * routed through `navigate` so the dirty guard still asks first.
 */
function onCrumbClick(e: MouseEvent, to: RouteLocationRaw): void {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
    return;
  }
  e.preventDefault();
  if (props.navigate) {
    props.navigate(to);
    return;
  }
  void router.push(to);
}
</script>

<template>
  <n-breadcrumb-item>{{ navLabels.organisation }}</n-breadcrumb-item>
  <n-breadcrumb-item :clickable="Boolean(props.templateUid)">
    <router-link v-if="props.templateUid" v-slot="{ href }" :to="listRoute" custom>
      <a :href="href" @click="onCrumbClick($event, listRoute)">{{ navLabels.templates }}</a>
    </router-link>
    <template v-else>{{ navLabels.templates }}</template>
  </n-breadcrumb-item>
  <n-breadcrumb-item v-if="props.templateUid" :clickable="Boolean(props.variantUid)">
    <router-link v-if="props.variantUid" v-slot="{ href }" :to="templateRoute" custom>
      <a :href="href" @click="onCrumbClick($event, templateRoute)">
        {{ props.templateName ?? '…' }}
      </a>
    </router-link>
    <template v-else>{{ props.templateName ?? '…' }}</template>
  </n-breadcrumb-item>
  <n-breadcrumb-item v-if="props.variantUid" :clickable="Boolean(props.page)">
    <router-link v-if="props.page" v-slot="{ href }" :to="variantRoute" custom>
      <a :href="href" @click="onCrumbClick($event, variantRoute)">
        {{ props.variantName ?? '…' }}
      </a>
    </router-link>
    <template v-else>{{ props.variantName ?? '…' }}</template>
  </n-breadcrumb-item>
  <n-breadcrumb-item v-if="props.page">{{ props.page }}</n-breadcrumb-item>
</template>
